import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useEffect, useState } from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(QUERY).matches;

/** Live-updating version for components that need to re-render on the change. */
export const useReducedMotion = () => {
  const [reduced, setReduced] = useState(prefersReducedMotion);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(QUERY);
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return reduced;
};

// Tween-only properties that must not leak into a gsap.set() call.
const TIMING_KEYS = [
  'duration', 'delay', 'ease', 'stagger', 'scrollTrigger',
  'repeat', 'yoyo', 'overwrite', 'transformOrigin',
];

/**
 * Scroll reveal that degrades safely.
 *
 * Sections here render at `opacity: 0` and depend on GSAP to bring them in, so
 * a reduced-motion user must not simply get the tween skipped — that would
 * leave the page blank. Instead we jump straight to the resolved end state.
 *
 * Returns the tween (or the set() call) so callers can revert it via context.
 */
export const reveal = (target, fromVars, toVars) => {
  if (!target) return null;

  if (prefersReducedMotion()) {
    const endState = { ...toVars };
    TIMING_KEYS.forEach((k) => delete endState[k]);
    // transformOrigin is a real property, not timing — keep it if it was set.
    if (toVars.transformOrigin) endState.transformOrigin = toVars.transformOrigin;
    if (fromVars && fromVars.transformOrigin) {
      endState.transformOrigin = fromVars.transformOrigin;
    }
    return gsap.set(target, endState);
  }

  return gsap.fromTo(target, fromVars, toVars);
};

/** Registers ScrollTrigger once, idempotently. */
export const registerScrollTrigger = () => {
  gsap.registerPlugin(ScrollTrigger);
};
